import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatPrice } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";

export default function Account() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [newAddr, setNewAddr] = useState({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India", label: "Home", is_default: false });

    useEffect(() => {
        if (!user) { navigate("/login"); return; }
        api.get("/orders/my").then(res => setOrders(res.data)).catch(() => {});
        api.get("/auth/addresses").then(res => setAddresses(res.data)).catch(() => {});
    }, [user, navigate]);

    if (!user) return null;

    const addAddress = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post("/auth/addresses", newAddr);
            setAddresses(res.data.addresses);
            setNewAddr({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India", label: "Home", is_default: false });
            toast.success("Address added");
        } catch (e) {
            toast.error("Could not save address");
        }
    };
    const removeAddress = async (id) => {
        const res = await api.delete(`/auth/addresses/${id}`);
        setAddresses(res.data.addresses);
    };

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-testid="account-page">
            <div className="flex justify-between items-end">
                <div>
                    <p className="font-mono-ui text-[#857C72]">Account</p>
                    <h1 className="font-display text-5xl mt-2">Hi, {user.name.split(" ")[0]}.</h1>
                    <p className="text-muted-foreground">{user.email}</p>
                </div>
                <button onClick={() => { logout(); navigate("/"); }} className="dw-btn-secondary px-5 py-2 font-mono-ui text-xs">Sign out</button>
            </div>

            <Tabs defaultValue="orders" className="mt-10">
                <TabsList className="bg-[#F4EFE6] p-1 rounded-full">
                    <TabsTrigger value="orders" className="rounded-full font-mono-ui" data-testid="account-tab-orders">Orders</TabsTrigger>
                    <TabsTrigger value="addresses" className="rounded-full font-mono-ui" data-testid="account-tab-addresses">Addresses</TabsTrigger>
                    <TabsTrigger value="profile" className="rounded-full font-mono-ui" data-testid="account-tab-profile">Profile</TabsTrigger>
                </TabsList>

                <TabsContent value="orders" className="mt-6 space-y-4">
                    {orders.length === 0 ? (
                        <div className="dw-section p-10 text-center">
                            <p className="font-display text-2xl">No orders yet.</p>
                            <Link to="/shop" className="inline-flex mt-4 dw-btn-primary px-6 py-2 font-mono-ui">Start shopping</Link>
                        </div>
                    ) : orders.map(o => (
                        <div key={o.id} className="dw-section p-6" data-testid={`account-order-${o.order_number}`}>
                            <div className="flex flex-wrap justify-between gap-3">
                                <div>
                                    <p className="font-mono-ui text-muted-foreground">Order {o.order_number}</p>
                                    <p className="text-sm text-muted-foreground">{new Date(o.created_at).toLocaleString()}</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-medium">{formatPrice(o.total)}</p>
                                    <span className="text-xs font-mono-ui px-3 py-1 rounded-full bg-[#F4EFE6] inline-block mt-1">{o.status.replace("_", " ")}</span>
                                </div>
                            </div>
                            <div className="mt-4 flex gap-2 overflow-x-auto">
                                {o.items.map((it, i) => (
                                    <img key={i} src={it.image} alt="" className="h-16 w-16 rounded-xl object-cover shrink-0" />
                                ))}
                            </div>
                            <div className="mt-4 flex gap-2">
                                <Link to={`/track/${o.order_number}`} className="dw-btn-secondary px-5 py-2 font-mono-ui text-xs">Track</Link>
                            </div>
                        </div>
                    ))}
                </TabsContent>

                <TabsContent value="addresses" className="mt-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                        {addresses.map(a => (
                            <div key={a.id} className="dw-section p-5">
                                <div className="flex justify-between items-start">
                                    <p className="font-mono-ui">{a.label}{a.is_default ? " · Default" : ""}</p>
                                    <button onClick={() => removeAddress(a.id)} className="text-muted-foreground hover:text-[#B42318]"><Trash2 className="h-4 w-4" /></button>
                                </div>
                                <p className="mt-3 text-sm">{a.full_name}<br />{a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />{a.city}, {a.state} {a.pincode}<br />{a.country} · {a.phone}</p>
                            </div>
                        ))}
                    </div>
                    <form onSubmit={addAddress} className="mt-6 dw-section p-6">
                        <h3 className="font-display text-2xl mb-4 flex items-center gap-2"><Plus className="h-5 w-5" /> Add address</h3>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {[
                                ["label", "Label"], ["full_name", "Full name"], ["phone", "Phone"], ["line1", "Line 1"], ["line2", "Line 2"], ["city", "City"], ["state", "State"], ["pincode", "PIN"]
                            ].map(([k, lbl]) => (
                                <div key={k}>
                                    <Label className="font-mono-ui mb-1.5 block">{lbl}</Label>
                                    <Input value={newAddr[k]} onChange={(e) => setNewAddr({ ...newAddr, [k]: e.target.value })} className="rounded-2xl bg-white/70 border-[#E7DED2] h-10" />
                                </div>
                            ))}
                        </div>
                        <button type="submit" className="mt-4 dw-btn-primary px-6 py-2 font-mono-ui text-xs">Save address</button>
                    </form>
                </TabsContent>

                <TabsContent value="profile" className="mt-6">
                    <div className="dw-section p-6">
                        <p><span className="font-mono-ui text-muted-foreground">Name:</span> {user.name}</p>
                        <p className="mt-2"><span className="font-mono-ui text-muted-foreground">Email:</span> {user.email}</p>
                        <p className="mt-2"><span className="font-mono-ui text-muted-foreground">Role:</span> {user.role}</p>
                    </div>
                </TabsContent>
            </Tabs>
            <div className="h-24" />
        </div>
    );
}
